/**
 * DeleteAdditionalScopeDialog — remove a change order / upsell for good.
 *
 * Server-side the delete cascades to everything the row generated (production
 * commitments + payment transactions) and archives the lot to
 * `change_orders_deleted` first, so a mistake is recoverable from the DB.
 * Rows that already carry money need an explicit force, which is why this
 * dialog names the amount before it lets you through.
 *
 * Shared by the Upsells Pipeline and the Change Orders dashboard so both
 * deletes look and behave identically.
 */
import { useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { AlertCircle, Trash2 } from "lucide-react";
import { hasRecordedMoney, canDeleteAdditionalScope, isFinanceRole } from "./additionalScopeAccess";

const fmt$ = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// Re-exported so the existing import sites keep working — the rules themselves
// now live in additionalScopeAccess.js alongside the matching edit rule.
export { hasRecordedMoney, canDeleteAdditionalScope, isFinanceRole };

export function DeleteAdditionalScopeDialog({ co, showMoney = false, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);

  const isUpsell = co?.request_type === "upsell";
  const noun = isUpsell ? "Upsell" : "Change Order";
  const paid = hasRecordedMoney(co);

  const handleDelete = async () => {
    if (!co) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/change-orders/${co.id}${paid ? "?force=true" : ""}`);
      toast.success(`${noun} deleted`);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to delete ${noun.toLowerCase()}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={!!co} onOpenChange={(open) => { if (!open && !deleting) onClose?.(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" /> Delete {noun}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Permanently remove <span className="font-semibold">{co?.title || co?.short_id || `this ${noun.toLowerCase()}`}</span>
            {co?.project_name ? ` from ${co.project_name.trim()}` : ""}. Its production
            commitments and payment records are deleted too, and the client&apos;s
            {isUpsell ? " proposal" : " approval"} link stops working. This cannot be undone from the app.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {paid && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              This {noun.toLowerCase()} has payments recorded against it
              {showMoney && co?.deposit_paid ? ` (deposit ${fmt$(co.deposit_amount)})` : ""}.
              Deleting it removes that payment history from reporting.
            </span>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive hover:bg-destructive/90"
            onClick={(e) => { e.preventDefault(); handleDelete(); }}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : `Delete ${noun}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
